import { isDocumentEntity } from './accessLadders'
import type { AuthzContextState } from './useAuthzContextQuery'
import type { ConfigArea, PermissionSetEntity } from '@omnea-digital/authz-model'

type ResolvedAuthzContext = Extract<AuthzContextState, { status: 'resolved' }>

/**
 * What an `fga` rule may read. Only the settled context appears: `loading` and `unavailable` are
 * decided by the resolver, never by a rule author. The caller's role is absent, so an `fga` rule
 * cannot widen itself past the rollout its own arm governs.
 */
type ResolvedAuthorizationSources = {
  authz: ResolvedAuthzContext
}

export type FgaAuthorizationRule = (sources: ResolvedAuthorizationSources) => boolean

export const hasEntityAuthorization =
  (entity: PermissionSetEntity): FgaAuthorizationRule =>
  ({ authz }) =>
    authz.entities.has(entity)

/** Whether the caller may add a record of this family, which entitlement to the family alone does not confer. */
export const creates =
  (entity: PermissionSetEntity): FgaAuthorizationRule =>
  ({ authz }) =>
    authz.entities.get(entity)?.canCreate === true

/** Whether the caller administers a configuration area — what a settings route's config gate asks. */
export const managesConfigArea =
  (area: ConfigArea): FgaAuthorizationRule =>
  ({ authz }) =>
    authz.managedAreas.has(area)

export const managesPermissions: FgaAuthorizationRule = ({ authz }) => authz.canManagePermissions

/** Whether the insights section opens for the caller. */
export const viewsInsights: FgaAuthorizationRule = ({ authz }) => authz.canViewInsights

/** Whether the caller may change what the insights section holds — what a page of nothing but mutations gates on. */
export const managesInsights: FgaAuthorizationRule = ({ authz }) => authz.canManageInsights

/** Whether the caller may make another user a super admin — a system admin, so nobody promotes past themselves. */
export const promotesToSuperAdmin: FgaAuthorizationRule = ({ authz }) => authz.canPromoteToSuperAdmin

/**
 * Whether the caller is a system admin. The context carries that as the promotion flag — only a system
 * admin may promote — so the two read the same answer.
 */
export const isSystemAdmin: FgaAuthorizationRule = ({ authz }) => authz.canPromoteToSuperAdmin

/** Whether the Documents page opens: the organisation documents level, or access to a family whose records carry documents. */
export const viewsDocuments: FgaAuthorizationRule = ({ authz }) =>
  authz.canViewAllDocuments || [...authz.entities.keys()].some(isDocumentEntity)

export const viewsRenewals: FgaAuthorizationRule = ({ authz }) => authz.canViewRenewals

export const viewsAllSubprocessors: FgaAuthorizationRule = ({ authz }) => authz.canViewAllSubprocessors

/** Whether the caller may add a document that names no supplier or agreement: the organisation documents level at edit. */
export const addsLibraryDocuments: FgaAuthorizationRule = ({ authz }) => authz.canEditAllDocuments

/** Whether the caller's request access reaches past the requests they take part in: organisation-wide or a subsidiary or department scope. */
export const reachesBeyondOwnRequests: FgaAuthorizationRule = ({ authz }) => authz.requestAccessScope !== 'involved-only'

/** Whether the caller may define custom fields — organisation-wide definitions, so not a verb on any one record. */
export const managesCustomFields: FgaAuthorizationRule = ({ authz }) => authz.canManageCustomFields

/** Whether the caller may view every request in the organisation (`can_view_all_requests`). */
export const viewsAllRequests: FgaAuthorizationRule = ({ authz }) => authz.requestAccessScope === 'all'

/** Whether the caller may view every document in the organisation (`can_view_all_documents`), not only those reached through a record. */
export const viewsAllDocuments: FgaAuthorizationRule = ({ authz }) => authz.canViewAllDocuments
